import { useState, useEffect, useRef, useMemo } from 'react';
import { useCategories } from '../hooks/useCategories';
import { apiService, api } from '../services/api';
import { toast } from 'react-hot-toast';
import type { Funnel, Category } from '../types';
import { ContactLabelSelector } from './ContactLabelSelector';

interface BulkEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedContactIds: string[];
  onSuccess: () => void;
}

export function BulkEditModal({ isOpen, onClose, selectedContactIds, onSuccess }: BulkEditModalProps) {
  const [categoriaId, setCategoriaId] = useState<string>('');
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [action, setAction] = useState<'update' | 'add_labels' | 'normalize_phones' | 'delete'>('update');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [search, setSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [funnels, setFunnels] = useState<Funnel[]>([]);
  const { categories } = useCategories({ pageSize: 999 });
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setCategoriaId('');
      setSelectedLabels([]);
      setAction('update');
      setIsSubmitting(false);
      setSearch('');
      setDropdownOpen(false);
      apiService.getFunnels({ includeCategories: false }).then(setFunnels).catch(() => undefined);
    }
  }, [isOpen]);

  // Fecha ao clicar fora
  useEffect(() => {
    if (!dropdownOpen) return;
    function handler(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  const funnelMap = useMemo(() => {
    const m = new Map<string, Funnel>();
    for (const f of funnels) m.set(f.id, f);
    return m;
  }, [funnels]);

  // Agrupa categorias por funil, filtrando pela pesquisa
  const grouped = useMemo(() => {
    const q = search.toLowerCase().trim();

    const matches = (cat: Category, funnel?: Funnel) => {
      if (!q) return true;
      return (
        cat.nome.toLowerCase().includes(q) ||
        (funnel?.nome || '').toLowerCase().includes(q)
      );
    };

    // Funis que têm pelo menos uma categoria visível
    const byFunnel: { funnel: Funnel; cats: Category[] }[] = [];
    for (const funnel of funnels) {
      const cats = categories.filter(
        (c) => c.funnelId === funnel.id && matches(c, funnel)
      );
      if (cats.length > 0) byFunnel.push({ funnel, cats });
    }

    // Categorias sem funil
    const noFunnel = categories.filter(
      (c) => !c.funnelId && matches(c, undefined)
    );

    return { byFunnel, noFunnel };
  }, [categories, funnels, search]);

  const selectedCategory = categories.find((c) => c.id === categoriaId);
  const selectedFunnel = selectedCategory?.funnelId ? funnelMap.get(selectedCategory.funnelId) : undefined;

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedContactIds.length === 0) {
      toast.error('Nenhum contato selecionado');
      return;
    }

    if (action === 'update' && !categoriaId) {
      toast.error('Selecione uma categoria');
      return;
    }

    if (action === 'add_labels' && selectedLabels.length === 0) {
      toast.error('Selecione pelo menos uma etiqueta');
      return;
    }

    setIsSubmitting(true);

    try {
      if (action === 'update') {
        await api.post('/contatos/bulk/update', {
          contactIds: selectedContactIds,
          updates: { categoriaId: categoriaId || null },
        });
        toast.success(`${selectedContactIds.length} contato(s) atualizado(s) com sucesso!`);
      } else if (action === 'add_labels') {
        await api.post('/contatos/bulk/update', {
          contactIds: selectedContactIds,
          updates: { tagsToAdd: selectedLabels },
        });
        toast.success(`${selectedLabels.length} etiqueta(s) adicionada(s) a ${selectedContactIds.length} contato(s)!`);
      } else if (action === 'normalize_phones') {
        const result = await api.post('/contatos/bulk/update', {
          contactIds: selectedContactIds,
          updates: { normalizePhones: true },
        });
        const normalized = Number(result.normalized || 0);
        const unchanged = Number(result.unchanged || 0);
        const failed = Number(result.failed || 0);
        if (failed > 0) {
          toast(`${normalized} normalizado(s), ${unchanged} já correto(s) e ${failed} inválido(s).`, {
            icon: '⚠️',
          });
        } else {
          toast.success(`${normalized} telefone(s) normalizado(s); ${unchanged} já estavam corretos.`);
        }
      } else {
        const confirmDelete = window.confirm(
          `Tem certeza que deseja excluir ${selectedContactIds.length} contato(s)? Esta ação não pode ser desfeita.`
        );
        if (!confirmDelete) {
          setIsSubmitting(false);
          return;
        }
        await api.post('/contatos/bulk/delete', { contactIds: selectedContactIds });
        toast.success(`${selectedContactIds.length} contato(s) excluído(s) com sucesso!`);
      }
      onSuccess();
    } catch (error: any) {
      console.error('Erro na operação em massa:', error);
      toast.error(error.response?.data?.error || 'Erro ao processar operação em massa');
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasResults = grouped.byFunnel.length > 0 || grouped.noFunnel.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90dvh] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--astra-dark-blue)' }}>
          Edição em Massa
        </h2>

        <p className="text-sm text-gray-600 mb-6">
          {selectedContactIds.length} contato(s) selecionado(s)
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Ação</label>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value as 'update' | 'add_labels' | 'normalize_phones' | 'delete')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="update">Atualizar Categoria</option>
              <option value="add_labels">Adicionar Etiquetas</option>
              <option value="normalize_phones">Normalizar Telefones</option>
              <option value="delete">Excluir Contatos</option>
            </select>
          </div>

          {action === 'update' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categoria *
              </label>

              <div ref={dropdownRef} className="relative">
                {/* Botão do seletor */}
                <button
                  type="button"
                  onClick={() => setDropdownOpen((o) => !o)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-300 rounded-md bg-white text-left focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  {selectedCategory ? (
                    <span className="flex items-center gap-2 min-w-0">
                      <span
                        className="h-3 w-3 rounded-full flex-shrink-0"
                        style={{ background: selectedCategory.cor || '#6b7280' }}
                      />
                      <span className="truncate font-medium">{selectedCategory.nome}</span>
                      {selectedFunnel && (
                        <span className="text-xs text-gray-400 truncate">
                          — {selectedFunnel.nome}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-gray-400">Selecione uma categoria</span>
                  )}
                  <svg className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>

                {dropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                    {/* Campo de pesquisa */}
                    <div className="p-2 border-b border-gray-100">
                      <input
                        autoFocus
                        type="text"
                        placeholder="Pesquisar categoria ou funil..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                      />
                    </div>

                    <div className="max-h-60 overflow-y-auto">
                      {!hasResults && (
                        <p className="px-3 py-3 text-sm text-gray-400 text-center">Nenhum resultado</p>
                      )}

                      {/* Categorias agrupadas por funil */}
                      {grouped.byFunnel.map(({ funnel, cats }) => (
                        <div key={funnel.id}>
                          {/* Cabeçalho do funil — não selecionável */}
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border-b border-gray-100">
                            <span
                              className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                              style={{ background: funnel.cor || '#6b7280' }}
                            />
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide truncate">
                              {funnel.nome}
                            </span>
                          </div>
                          {cats.map((cat) => (
                            <button
                              key={cat.id}
                              type="button"
                              onClick={() => { setCategoriaId(cat.id); setDropdownOpen(false); setSearch(''); }}
                              className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-blue-50 transition-colors ${categoriaId === cat.id ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-700'}`}
                            >
                              <span
                                className="h-3 w-3 rounded-full flex-shrink-0"
                                style={{ background: cat.cor || '#6b7280' }}
                              />
                              <span className="truncate">{cat.nome}</span>
                            </button>
                          ))}
                        </div>
                      ))}

                      {/* Categorias sem funil */}
                      {grouped.noFunnel.length > 0 && (
                        <div>
                          {(grouped.byFunnel.length > 0) && (
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border-b border-gray-100">
                              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sem funil</span>
                            </div>
                          )}
                          {grouped.noFunnel.map((cat) => (
                            <button
                              key={cat.id}
                              type="button"
                              onClick={() => { setCategoriaId(cat.id); setDropdownOpen(false); setSearch(''); }}
                              className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-blue-50 transition-colors ${categoriaId === cat.id ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-700'}`}
                            >
                              <span
                                className="h-3 w-3 rounded-full flex-shrink-0"
                                style={{ background: cat.cor || '#6b7280' }}
                              />
                              <span className="truncate">{cat.nome}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {action === 'add_labels' && (
            <ContactLabelSelector
              value={selectedLabels}
              onChange={setSelectedLabels}
              label="Etiquetas *"
              helperText="As etiquetas selecionadas serão adicionadas sem remover as que os contatos já possuem."
            />
          )}

          {action === 'normalize_phones' && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <p className="text-sm text-blue-800">
                Os telefones selecionados serão convertidos para o formato internacional, por exemplo: +5511999999999. Os demais dados dos contatos não serão alterados.
              </p>
            </div>
          )}

          {action === 'delete' && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-800">
                ⚠️ Atenção: Esta ação irá excluir permanentemente {selectedContactIds.length} contato(s) e não poderá ser desfeita.
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`flex-1 px-4 py-2 rounded-md text-white focus:outline-none focus:ring-2 disabled:opacity-50 ${
                action === 'delete'
                  ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                  : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
              }`}
            >
              {isSubmitting ? 'Processando...' : action === 'delete' ? 'Excluir' : action === 'add_labels' ? 'Adicionar' : action === 'normalize_phones' ? 'Normalizar' : 'Atualizar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
